import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./domainBaseQuery";
import {
  CreateDomainRequest,
  CreateDomainResponse,
  CreateDomainVersionRequest,
  CreateDomainVersionResponse,
  DeleteDomainRequest,
  DeleteDomainResponse,
  DeleteDomainVersionRequest,
  DeleteDomainVersionResponse,
  GetDomainRequest,
  GetDomainResponse,
  GetDomainVersionRequest,
  GetDomainVersionResponse,
  PdfToMarkdownRequest,
  PdfToMarkdownResponse,
  PublishDomainVersionRequest,
  PublishDomainVersionResponse,
  UpdateDomainRequest,
  UpdateDomainResponse,
  UpdateDomainVersionRequest,
  UpdateDomainVersionResponse,
} from "./reqres";

export const domainServiceApi = createApi({
  reducerPath: "domainServiceApi",
  baseQuery,
  endpoints: () => ({}),
  tagTypes: ["Domains", "DomainVersions"],
});

export const domainApi = domainServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    pdfToMarkdown: builder.mutation<
      PdfToMarkdownResponse,
      PdfToMarkdownRequest
    >({
      query: ({ conversion_method, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `/pdf_to_markdown?conversion_method=${conversion_method}`,
          method: "POST",
          body: formData,
        };
      },
    }),

    createDomain: builder.mutation<CreateDomainResponse, CreateDomainRequest>({
      query: ({ file, request }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("request", request);

        return {
          url: "/domains",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Domains"],
    }),

    getDomain: builder.query<GetDomainResponse, GetDomainRequest>({
      query: ({ domain_id }) => `/domains/${domain_id}`,
      providesTags: (_result, _error, { domain_id }) => [
        { type: "Domains", id: domain_id },
      ],
    }),

    updateDomain: builder.mutation<UpdateDomainResponse, UpdateDomainRequest>({
      query: ({ domain_id, name }) => ({
        url: `/domains/${domain_id}`,
        method: "PUT",
        body: { name },
      }),
      invalidatesTags: (_result, _error, { domain_id }) => [
        { type: "Domains", id: domain_id },
      ],
    }),

    deleteDomain: builder.mutation<DeleteDomainResponse, DeleteDomainRequest>({
      query: ({ domain_id }) => ({
        url: `/domains/${domain_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { domain_id }) => [
        "Domains",
        { type: "Domains", id: domain_id },
      ],
    }),

    createDomainVersion: builder.mutation<
      CreateDomainVersionResponse,
      CreateDomainVersionRequest
    >({
      query: ({ domain_id, ...body }) => ({
        url: `/domains/${domain_id}/versions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { domain_id }) => [
        { type: "Domains", id: domain_id },
      ],
    }),

    getDomainVersion: builder.query<
      GetDomainVersionResponse,
      GetDomainVersionRequest
    >({
      query: ({ domain_id, version_id }) =>
        `/domains/${domain_id}/versions/${version_id}`,
      providesTags: (_result, _error, { version_id }) => [
        { type: "DomainVersions", id: version_id },
      ],
    }),

    updateDomainVersion: builder.mutation<
      UpdateDomainVersionResponse,
      UpdateDomainVersionRequest
    >({
      query: ({ domain_id, version_id, ...body }) => ({
        url: `/domains/${domain_id}/versions/${version_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { domain_id }) => [
        { type: "Domains", id: domain_id },
      ],
    }),

    deleteDomainVersion: builder.mutation<
      DeleteDomainVersionResponse,
      DeleteDomainVersionRequest
    >({
      query: ({ domain_id, version_id }) => ({
        url: `/domains/${domain_id}/versions/${version_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { domain_id, version_id }) => [
        { type: "DomainVersions", id: version_id },
        { type: "Domains", id: domain_id },
      ],
    }),

    publishDomainVersion: builder.mutation<
      PublishDomainVersionResponse,
      PublishDomainVersionRequest
    >({
      query: ({ domain_id, version_id }) => ({
        url: `/domains/${domain_id}/versions/${version_id}:publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { domain_id, version_id }) => [
        { type: "Domains", id: domain_id },
        { type: "DomainVersions", id: version_id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  usePdfToMarkdownMutation,
  useCreateDomainMutation,
  useGetDomainQuery,
  useUpdateDomainMutation,
  useDeleteDomainMutation,
  useCreateDomainVersionMutation,
  useGetDomainVersionQuery,
  useUpdateDomainVersionMutation,
  useDeleteDomainVersionMutation,
  usePublishDomainVersionMutation,
  usePrefetch: usePrefetchDomain,
} = domainApi;

export const domainApiTagTypes = ["Domains", "DomainVersions"];
